import { BillingCycle, SubscriptionType, type WeavePricingTier } from './shared';
import type { ThemeMode } from '@figma/fpl-themes';
import type { TourKeys } from '@/components/ProductTours/tour-keys';
import type { UserTourStatus } from '@/enums/user-tour-status.enum';

/** Saved appearance choice; System resolves independently on each device. */
export type ThemePreference = ThemeMode | 'system';

export enum CreditsType {
  Monthly = 'monthly',
  Extra = 'extra',
  Rollover = 'rollover',
}

// Enum for user roles in workspace
export enum WorkspaceRole {
  Member = 'Member',
  Admin = 'Admin',
  Owner = 'Owner',
}

// Base workspace information
export interface BaseWorkspace {
  workspaceId: string;
  workspaceName: string;
  role: WorkspaceRole;
  subscription: Subscription;
  isModelBlockingEnabled?: boolean;
  isModelApprovalSettingEnabled?: boolean;
  isByokEnabled?: boolean;
}

interface Subscription {
  hasBillingCustomer?: boolean;
  credits: number;
  expiresAt: Date;
  name: string;
  seatsLimit: number;
  billingCycle: BillingCycle;
  usedSeats: number;
  startsAt: Date;
  type: SubscriptionType;
  monthlyCredits: number;
  creditsPerDollar: number;
  scheduledDowngradeDate?: Date;
  scheduledDowngradePlan?: SubscriptionType;
  creditsList: { type: CreditsType; available: number; allocatedAmount: number; expiresAt: Date }[];
}

export enum CreditThresholdRuleType {
  Percentage = 'percentage',
  Absolute = 'absolute',
}

export enum CreditThresholdRuleAction {
  Notify = 'notify',
}

export interface CreditThresholdRule {
  id: string | undefined;
  threshold: number;
  thresholdType: CreditThresholdRuleType;
  action: CreditThresholdRuleAction;
  enabled: boolean;
  triggeredAt?: string | null;
}

export interface WorkspaceDeveloperApiKeysTermsAcceptance {
  /** ISO 8601. */
  acceptedAt: string;
  /** Weave user id of the admin who accepted on the workspace's behalf. */
  acceptedByUserId: string;
}

// Active workspace extends base workspace with additional properties
export interface ActiveWorkspace extends BaseWorkspace {
  hasActiveWeaveMcpPromoCredits?: boolean;
  preferences: {
    showCreditsToMembers: boolean;
    defaultMonthlyAllocatedCredits: number | null;
    shouldBlockUnverifiedModels?: boolean;
    creditThresholdRules?: CreditThresholdRule[] | null;
    shouldAutoApproveVerifiedModels?: boolean;
    /** Availability emails require explicit opt-in; unset means off. */
    shouldNotifyVerifiedModels?: boolean;
    isFigmaOrgPublishingEnabled?: boolean;
    /** Voice input in prompts. Unset means on; only `false` turns it off. */
    isVoiceInputEnabled?: boolean;
    developerApiKeysTermsAcceptance?: WorkspaceDeveloperApiKeysTermsAcceptance | null;
  };
  monthlyCreditsLimit?: number;
  hasCustomCreditsLimit?: boolean;
  userWorkspaceCreditsUsage: number;
}

// Tour data structure
export interface TourDetails {
  currentStep: number;
  status: UserTourStatus;
  customData: {
    context?: string;
    recipeId?: string;
    // Set once the user has claimed the one-time template-gallery onboarding credits (WEA-5082).
    creditsGranted?: boolean;
    // Set true once the user permanently dismisses the onboarding template gallery via its X (WEA-5473).
    galleryDismissed?: boolean;
  };
}

// User tours indexed by tour ID
type Tours = Record<TourKeys, TourDetails>;

// Edge style preference for flow edges
export type EdgeTypePreference = 'bezier' | 'step' | 'line';

// Type-safe user preferences interface
export interface UserPreferences {
  panOnScroll?: boolean;
  showFloatMenuOnRightClick?: boolean;
  requireAltKeyForSuggestions?: boolean;
  edgeType?: EdgeTypePreference;
  snapToGrid?: boolean;
  snapToNodes?: boolean;
  /** Color mode. Unset means the user has never chosen, which resolves to `dark`. */
  theme?: ThemePreference;
}

// Type for user preference keys derived from the interface
export type UserPreferenceKey = keyof UserPreferences;

// Type for user preference values
export type UserPreferenceValue = boolean | EdgeTypePreference | ThemePreference;

export interface EnrichedUserFields {
  id: string;
  activeWorkspace: ActiveWorkspace;
  createdAt: number;
  credits: number;
  name: string;
  subscriptionType: SubscriptionType;
  tours: Tours;
  workspaces: BaseWorkspace[];
  role: WorkspaceRole;
  preferences?: UserPreferences;
  isMarketingConsentEligible?: boolean;
}

export interface AuthUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
  accessToken?: string;
  providerData?: { providerId: string }[];
  // True for a logged-out "Anyone with the link" Figma embed viewer (from the
  // embed token's `anonymous` claim). Undefined/false otherwise.
  isAnonymous?: boolean;
  /**
   * Resolved from the embed token's `weave_pricing_tier` claim and used to pick the
   * credit ratio the client displays. Only set in Figma embed mode; undefined for
   * standalone users.
   */
  weavePricingTier?: WeavePricingTier;
  getIdToken(forceRefresh?: boolean): Promise<string>;
}

/**
 * App-facing user fields merged from authentication and database records.
 * Provider prototype methods are intentionally excluded because object spread does not
 * preserve them; callers that need provider behavior must use the raw auth user.
 */
export type User = Omit<AuthUser, 'getIdToken'> & EnrichedUserFields;
